import React, { useEffect, useState, useCallback } from "react";
import { getRetailerOrderHistory } from "../services/api";
import RetailerSidebar from "../components/retailer/RetailerSidebar";

export default function OrderHistoryPage() {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_items: 0,
    total_pages: 1,
  });

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [productIdFilter, setProductIdFilter] = useState("");
  const [inquiryIdFilter, setInquiryIdFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null); // For slide-over details

  // Fetch function wrapped in useCallback
  const fetchOrderHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRetailerOrderHistory({
        page: pagination.page,
        limit: pagination.limit,
        customer: appliedSearch.trim() || undefined,
        product_id: productIdFilter.trim() || undefined,
        inquiry_id: inquiryIdFilter.trim() || undefined,
      });

      setOrders(res.data || []);
      if (res.pagination) {
        setPagination((prev) => ({
          ...prev,
          total_items: res.pagination.total_items,
          total_pages: res.pagination.total_pages,
        }));
      }
    } catch (err) {
      setError(err.message || "Failed to load order history");
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    appliedSearch,
    productIdFilter,
    inquiryIdFilter,
  ]);

  useEffect(() => {
    fetchOrderHistory();
  }, [fetchOrderHistory]);

  // Handle Search Submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    setAppliedSearch(searchTerm);
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setProductIdFilter("");
    setInquiryIdFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? dateStr
      : date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  return (
    <div className="flex min-h-screen bg-gray-50/50">
      {/* Header */}
      <RetailerSidebar />
      <main className="ml-64 p-6 w-full">
        <div className="min-h-screen bg-gray-50 p-6 font-sans w-full">
          <div className="w-full space-y-6">
            {/* Header Title Section */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Sales & Order History
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Audit log of all stock reduction sales transactions and linked
                  customer inquiries.
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 shadow-sm">
                <span>Total Sales Recorded:</span>
                <span className="text-indigo-600 font-extrabold">
                  {pagination.total_items}
                </span>
              </div>
            </div>

            {/* Filters Card */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
              <form
                onSubmit={handleSearchSubmit}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
              >
                {/* Customer Search */}
                <div>
                  <label className="block text-[11px] font-bold uppercase text-gray-600 mb-1">
                    Customer / Email / Contact
                  </label>
                  <input
                    type="text"
                    placeholder="Search name, email, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>

                {/* Product ID Filter */}
                {/* <div>
                  <label className="block text-[11px] font-bold uppercase text-gray-600 mb-1">
                    Product UUID
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by Product ID..."
                    value={productIdFilter}
                    onChange={(e) => {
                      setProductIdFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />
                </div> */}

                {/* Inquiry ID Filter */}
                {/* <div>
                  <label className="block text-[11px] font-bold uppercase text-gray-600 mb-1">
                    Inquiry UUID
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by Inquiry ID..."
                    value={inquiryIdFilter}
                    onChange={(e) => {
                      setInquiryIdFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />
                </div> */}

                {/* Filter Buttons */}
                <div className="flex items-end gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-md transition-colors"
                  >
                    Apply
                  </button>
                  {(appliedSearch || productIdFilter || inquiryIdFilter) && (
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-md transition-colors"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Loading / Error States */}
            {loading && (
              <div className="py-12 bg-white rounded-xl border border-gray-200 text-center text-xs text-gray-500 shadow-sm">
                Loading sales history...
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 shadow-sm">
                {error}
              </div>
            )}

            {/* Data Table */}
            {!loading && !error && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-700 font-semibold">
                        <th className="py-3 px-4">Date & Time</th>
                        <th className="py-3 px-4">Product Details</th>
                        <th className="py-3 px-4 text-center">Qty Sold</th>
                        <th className="py-3 px-4 text-right">Stock Impact</th>
                        <th className="py-3 px-4">Customer Details</th>
                        <th className="py-3 px-4">Location</th>
                        <th className="py-3 px-4">Inquiry Ref</th>
                        <th className="py-3 px-4 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.length > 0 ? (
                        orders.map((item) => {
                          const cust = item.customer || {};
                          const prod = item.product || {};
                          const location = [cust.city, cust.state, cust.country]
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <tr
                              key={item.transaction_id}
                              className="hover:bg-gray-50/50"
                            >
                              {/* Date */}
                              <td className="py-3 px-4 text-gray-600 font-medium whitespace-nowrap">
                                {formatDate(item.created_at)}
                              </td>

                              {/* Product Info */}
                              <td className="py-3 px-4">
                                <div className="font-semibold text-gray-900">
                                  SKU: {prod.sku}{" "}
                                </div>
                                <div className="text-[11px] text-gray-500 font-mono">
                                    size
                                  {prod.product_size
                                    ? `: ${prod.product_size}`
                                    : ""}
                                </div>
                              </td>

                              {/* Quantity Sold */}
                              <td className="py-3 px-4 text-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-50 text-red-700 border border-red-200">
                                  -{item.quantity_sold}
                                </span>
                              </td>

                              {/* Stock Impact */}
                              <td className="py-3 px-4 text-right font-mono text-[11px]">
                                <span className="text-gray-400">
                                  {item.previous_stock}
                                </span>
                                <span className="mx-1 text-gray-300">→</span>
                                <span className="font-bold text-gray-900">
                                  {item.remaining_stock}
                                </span>
                              </td>

                              {/* Customer Details */}
                              <td className="py-3 px-4">
                                {cust.name ? (
                                  <div>
                                    <div className="font-semibold text-gray-900">
                                      {cust.name}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                      {cust.email || "-"}
                                    </div>
                                    {cust.contact_number && (
                                      <div className="text-[10px] text-gray-400 font-mono">
                                        {cust.contact_country_code || ""}{" "}
                                        {cust.contact_number}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">
                                    Direct / Manual Sale
                                  </span>
                                )}
                              </td>

                              {/* Location */}
                              <td className="py-3 px-4 text-gray-600">
                                {location || "-"}
                              </td>

                              {/* Inquiry Reference */}
                              <td className="py-3 px-4 font-mono text-[11px]">
                                {cust.inquiry_id ? (
                                  <span className="text-indigo-600 font-medium">
                                    {cust.inquiry_id}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">N/A</span>
                                )}
                              </td>

                              {/* Action Button */}
                              <td className="py-3 px-4 text-center">
                                <button
                                  type="button"
                                  onClick={() => setSelectedRecord(item)}
                                  className="px-2.5 py-1 text-[11px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                                >
                                  Inspect
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            className="py-10 text-center text-gray-500"
                          >
                            No sales transactions match your search filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-gray-200 bg-gray-50 text-xs">
                  {/* Limit Selector */}
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>Show</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => {
                        setPagination((prev) => ({
                          ...prev,
                          limit: Number(e.target.value),
                          page: 1,
                        }));
                      }}
                      className="px-2 py-1 border border-gray-300 rounded bg-white font-medium focus:outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>entries per page</span>
                  </div>

                  {/* Page Navigator */}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">
                      Page{" "}
                      <strong className="text-gray-900">
                        {pagination.page}
                      </strong>{" "}
                      of{" "}
                      <strong className="text-gray-900">
                        {pagination.total_pages}
                      </strong>
                    </span>

                    <div className="flex gap-1 ml-2">
                      <button
                        type="button"
                        disabled={pagination.page <= 1}
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            page: prev.page - 1,
                          }))
                        }
                        className="px-3 py-1 border border-gray-300 bg-white rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={pagination.page >= pagination.total_pages}
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                          }))
                        }
                        className="px-3 py-1 border border-gray-300 bg-white rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Slide-over Inspection Panel */}
          {selectedRecord && (
            <OrderDetailsSlideOver
              record={selectedRecord}
              onClose={() => setSelectedRecord(null)}
              formatDate={formatDate}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// Side Panel / Modal Component for Order Details
function OrderDetailsSlideOver({ record, onClose, formatDate }) {
  const cust = record.customer || {};
  const prod = record.product || {};

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs">
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col p-6 overflow-y-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">
              Transaction Details
            </h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">
              ID: {record.transaction_id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg font-bold"
          >
            ✕
          </button>
        </div>

        {/* Transaction Summary */}
        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">Transaction Date:</span>
            <span className="font-semibold text-gray-800">
              {formatDate(record.created_at)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Quantity Sold:</span>
            <span className="font-bold text-red-600">
              -{record.quantity_sold} units
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Previous / Remaining:</span>
            <span className="font-mono text-gray-800">
              {record.previous_stock} →{" "}
              <strong>{record.remaining_stock}</strong>
            </span>
          </div>
          {/* {record.note && (
            <div className="pt-2 border-t border-gray-200 text-gray-600 italic">
              Note: {record.note}
            </div>
          )} */}
        </div>

        {/* Product Details */}
        <div className="space-y-2 text-xs">
          <h3 className="font-bold text-gray-800 uppercase tracking-wider text-[11px]">
            Product Information
          </h3>
          <div className="p-3 border border-gray-200 rounded-lg space-y-1.5 bg-white">
            {/* <div className="flex justify-between">
              <span className="text-gray-500">Product Name:</span>
              <span className="font-bold text-gray-900">
                {prod.product_name}
              </span>
            </div> */}
            <div className="flex justify-between">
              <span className="text-gray-500">SKU:</span>
              <span className="font-mono text-gray-800">{prod.sku}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Price:</span>
              <span className="font-semibold text-gray-900">
                {prod.price || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Price Unit:</span>
              <span className="font-semibold text-gray-900">
                {prod.price_unit || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Current Stock Status:</span>
              <span className="font-bold uppercase text-indigo-600">
                {prod.stock_status}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="space-y-2 text-xs">
          <h3 className="font-bold text-gray-800 uppercase tracking-wider text-[11px]">
            Customer Information
          </h3>
          <div className="p-3 border border-gray-200 rounded-lg space-y-1.5 bg-white">
            <div className="flex justify-between">
              <span className="text-gray-500">Full Name:</span>
              <span className="font-bold text-gray-900">
                {cust.name || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Email:</span>
              <span className="text-gray-800">{cust.email || "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Contact Number:</span>
              <span className="font-mono text-gray-800">
                {cust.contact_country_code || ""} {cust.contact_number || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Location:</span>
              <span className="text-gray-800">
                {[cust.city, cust.state, cust.country]
                  .filter(Boolean)
                  .join(", ") || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Inquiry ID:</span>
              <span className="font-mono text-gray-800">
                {cust.inquiry_id || "Direct Sale"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t mt-auto">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 bg-gray-800 hover:bg-gray-900 text-white font-semibold text-xs rounded-lg transition-colors"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
}
